import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Download, FileText, Link2, Loader2, Sparkles, Trash2, Truck, Upload, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const TIPO_LABEL = { poliza: "Póliza de seguro", libre_deuda: "Libre de deuda", licencia_conductor: "Licencia de conducir", tarjeta_circulacion: "Tarjeta de circulación", otro: "Otro" };

export function UploadDocsButton({ onDone }) {
  const [pend, setPend] = useState(null); // FileList pendiente de elegir destino
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const send = async (destino) => {
    setBusy(true);
    try {
      const fd = new FormData();
      pend.forEach((f) => fd.append("files", f));
      fd.append("destino", destino);
      const { data } = await api.post("/docshub/upload", fd);
      if (destino === "hub") {
        const ok = (data.resultados || []).filter((r) => r.tipo !== "error");
        const err = (data.resultados || []).filter((r) => r.tipo === "error");
        if (ok.length) toast.success(`${ok.length} archivo(s) guardado(s) en Documents Hub`);
        err.forEach((r) => toast.error(`${r.name}: ${r.detalle}`));
      } else {
        toast.success("Analizando documentos con IA para Flotilla — mira el avance abajo");
        (data.job?.omitidos || []).forEach((o) => toast.error(o));
      }
      setPend(null);
      onDone?.(destino);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <label data-testid="docs-upload-btn"
        className="inline-flex items-center justify-center gap-2 rounded-full bg-navy text-cream text-sm px-4 py-2.5 hover:bg-navy-soft transition-colors cursor-pointer">
        <Upload className="w-4 h-4" /> Subir archivos
        <input ref={inputRef} type="file" multiple className="hidden" data-testid="docs-upload-input"
          onChange={(e) => { if (e.target.files?.length) setPend(Array.from(e.target.files)); e.target.value = ""; }} />
      </label>
      {pend && (
        <div className="fixed inset-0 z-[90] bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="docs-dest-modal">
          <div className="bg-cream rounded-2xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">¿Para qué módulo cargas estos archivos?</h3>
              <button onClick={() => setPend(null)} className="text-navy/40 hover:text-navy" data-testid="docs-dest-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-navy/50 mt-2 max-h-24 overflow-y-auto">
              {pend.map((f, i) => <div key={`${f.name}-${i}`}>· {f.name} ({(f.size / 1024 / 1024).toFixed(1)} MB)</div>)}
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <button onClick={() => send("hub")} disabled={busy} data-testid="docs-dest-hub"
                className="rounded-xl border border-navy/15 p-4 text-left hover:border-violet transition-colors disabled:opacity-50">
                <FileText className="w-5 h-5 text-violet mb-2" />
                <div className="text-sm font-medium">OMNIA Documents Hub</div>
                <div className="text-[11px] text-navy/50 mt-1">Los Excel/CSV se vuelven hojas editables; el resto queda guardado para consultar y descargar (PDF, Word, Numbers, Pages…)</div>
              </button>
              <button onClick={() => send("flotilla")} disabled={busy} data-testid="docs-dest-flotilla"
                className="rounded-xl border border-navy/15 p-4 text-left hover:border-violet transition-colors disabled:opacity-50">
                <Truck className="w-5 h-5 text-violet mb-2" />
                <div className="text-sm font-medium">OMNIA Flotilla (Strix)</div>
                <div className="text-[11px] text-navy/50 mt-1">La IA interpreta los PDFs (pólizas, libre deuda, licencias, cédulas…), los separa por unidad y los carga a cada bitácora con sus vencimientos</div>
              </button>
            </div>
            {busy && <div className="mt-3 text-xs text-navy/50 inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Subiendo…</div>}
          </div>
        </div>
      )}
    </>
  );
}

export function ConnectLinkBox({ onDone }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/docshub/connect-link", { url });
      (data.conectadas || []).forEach((n) => toast.success(`Hoja conectada: ${n}`));
      if (data.archivos?.length) toast.success(`${data.archivos.length} archivo(s) importado(s) a Documents Hub`);
      (data.omitidos || []).forEach((o) => toast.error(o, { duration: 7000 }));
      if (!data.conectadas?.length && !data.archivos?.length && !data.omitidos?.length) toast.info("El link no trajo contenido nuevo");
      setUrl("");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo acceder al link", { duration: 9000 }); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2 items-center flex-wrap" data-testid="connect-link-box">
      <div className="relative flex-1 min-w-[260px]">
        <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy/30" />
        <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="connect-link-input"
          onKeyDown={(e) => e.key === "Enter" && go()}
          placeholder="Pega un link de Google Sheets, de carpeta de Drive o de archivo…"
          className="w-full rounded-full border border-navy/15 bg-white/60 pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-violet" />
      </div>
      <button onClick={go} disabled={busy || !url.trim()} data-testid="connect-link-btn"
        className="inline-flex items-center gap-2 rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:border-violet hover:text-violet transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Conectar link
      </button>
    </div>
  );
}

export function HubFilesSection({ refreshKey }) {
  const [files, setFiles] = useState([]);
  const load = useCallback(() => { api.get("/docshub/files").then((r) => setFiles(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!files.length) return null;
  return (
    <div className="card-surface p-6" data-testid="docshub-files-section">
      <h3 className="font-medium flex items-center gap-2"><FileText className="w-4 h-4 text-violet" /> Archivos ({files.length})</h3>
      <p className="text-xs text-navy/40 mt-1">PDF, Word, Numbers, Pages y cualquier otro archivo subido o importado de Drive.</p>
      <div className="divide-y divide-navy/5 mt-3">
        {files.map((f) => (
          <div key={f.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`docshub-file-${f.id}`}>
            <span className="uppercase text-[10px] font-semibold text-violet bg-violet/10 rounded px-1.5 py-0.5 shrink-0">{f.ext || "?"}</span>
            <span className="truncate flex-1">{f.name}</span>
            <span className="text-[11px] text-navy/40 shrink-0">{(f.size / 1024 / 1024).toFixed(1)} MB · {String(f.created_at).slice(0, 10)}</span>
            <a href={f.url} target="_blank" rel="noreferrer" className="text-violet hover:underline inline-flex items-center gap-1 text-xs shrink-0" data-testid={`docshub-file-dl-${f.id}`}>
              <Download className="w-3.5 h-3.5" /> abrir
            </a>
            <button onClick={async () => { try { await api.delete(`/docshub/files/${f.id}`); toast.success("Archivo eliminado"); load(); } catch { toast.error("Error"); } }}
              className="text-navy/30 hover:text-red-500" data-testid={`docshub-file-del-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function JobItems({ job, onApplied }) {
  const [items, setItems] = useState(job.items || []);
  const [units, setUnits] = useState([]);
  const [edited, setEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/fleet/units").then((r) => setUnits(r.data)).catch(() => {}); }, []);

  const upd = (id, patch) => { setItems((arr) => arr.map((it) => (it.id === id ? { ...it, ...patch } : it))); setEdited(true); };
  const aplicar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/docshub/jobs/${job.id}/apply`, { items, edited });
      toast.success(`${data.total} documento(s) cargado(s) a Flotilla`);
      if (data.sin_unidad?.length) toast.error(`Sin unidad: ${data.sin_unidad.join(", ")}`, { duration: 8000 });
      if (data.auto_desde_ahora) toast.info("Confianza alcanzada: las próximas cargas de este tipo se aplicarán automáticamente", { duration: 9000 });
      onApplied?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al aplicar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-2">
      {items.length === 0 ? (
        <div className="text-xs text-navy/50 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-1" data-testid="job-empty-state">
          No se detectaron documentos vehiculares en estos PDFs (pólizas, libre deuda, licencias o cédulas). Verifica que el archivo sea el correcto o súbelo a Documents Hub como archivo general.
        </div>
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-navy/40">
            <th className="py-1 pr-2">Usar</th><th className="py-1 pr-2">Patente/Titular</th><th className="py-1 pr-2">Tipo</th>
            <th className="py-1 pr-2">Páginas</th><th className="py-1 pr-2">Datos</th><th className="py-1 pr-2">Vence/Fecha</th><th className="py-1">Unidad</th>
          </tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-navy/5 align-top" data-testid="job-item-row">
                <td className="py-1.5 pr-2"><input type="checkbox" checked={!!it.incluir} disabled={!it.unit_id}
                  onChange={(e) => upd(it.id, { incluir: e.target.checked })} /></td>
                <td className="py-1.5 pr-2 font-medium">{it.patente || it.titular || "?"}
                  {it.match_via === "chofer" && <div className="text-[10px] text-navy/40 font-normal">por chofer</div>}</td>
                <td className="py-1.5 pr-2">{TIPO_LABEL[it.tipo] || it.tipo}
                  {it.deuda === "con_deuda" && <span className="ml-1 text-red-600 font-semibold">CON DEUDA</span>}
                  {it.deuda === "libre" && <span className="ml-1 text-emerald-600">libre ✓</span>}</td>
                <td className="py-1.5 pr-2">{it.pagina_desde}–{it.pagina_hasta}</td>
                <td className="py-1.5 pr-2 max-w-[220px]"><div className="truncate">{[it.aseguradora, it.nro_poliza].filter(Boolean).join(" · ") || it.detalle}</div></td>
                <td className="py-1.5 pr-2">{it.vigencia_hasta || it.fecha_documento || "—"}</td>
                <td className="py-1.5">
                  <select value={it.unit_id || ""} className="rounded border border-navy/15 bg-white/60 px-1.5 py-1 max-w-[160px]"
                    data-testid="job-item-unit-select"
                    onChange={(e) => { const u = units.find((x) => x.id === e.target.value); upd(it.id, { unit_id: e.target.value || null, unit_nombre: u?.nombre || "", incluir: !!e.target.value }); }}>
                    <option value="">— sin unidad —</option>
                    {it.unit_id && !units.some((u) => u.id === it.unit_id) && <option value={it.unit_id}>{it.unit_nombre} (baja)</option>}
                    {units.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
                  </select>
                  {it.unit_estado === "baja" && <div className="text-[10px] text-amber-600">unidad dada de baja</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      <button onClick={aplicar} disabled={busy || !items.some((i) => i.incluir)} data-testid="job-apply-btn"
        className="mt-3 inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-2.5 hover:opacity-90 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirmar y cargar a Flotilla
      </button>
    </div>
  );
}

export function FlotillaJobsSection({ refreshKey }) {
  const [jobs, setJobs] = useState([]);
  const [trust, setTrust] = useState(null);
  const load = useCallback(() => {
    api.get("/docshub/jobs").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/docshub/trust").then((r) => setTrust(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    if (!jobs.some((j) => j.status === "analizando")) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [jobs, load]);
  const autos = (trust?.tipos || []).filter((t) => t.auto);
  const pausados = (trust?.tipos || []).filter((t) => t.paused && t.confirms >= (trust?.min || 2));
  const enProgreso = (trust?.tipos || []).filter((t) => !t.auto && !t.paused && t.confirms > 0);
  const toggleTrust = async (tipo) => {
    try { const r = await api.post(`/docshub/trust/${tipo}/toggle`); toast.success(r.data.paused ? "Carga automática en pausa: volverás a confirmar cada carga" : "Carga automática reactivada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  if (!jobs.length && !autos.length && !pausados.length && !enProgreso.length) return null;
  const chip = (s) => s === "analizando" ? ["Analizando con IA…", "bg-violet/10 text-violet"]
    : s === "preview" ? ["Esperando tu confirmación", "bg-amber-100 text-amber-700"]
    : s === "aplicado" ? ["Cargado a Flotilla", "bg-emerald-100 text-emerald-700"]
    : s === "aplicado_auto" ? ["Cargado automáticamente", "bg-emerald-100 text-emerald-700"]
    : ["Error", "bg-red-100 text-red-600"];
  return (
    <div className="card-surface p-6" data-testid="flotilla-jobs-section">
      <h3 className="font-medium flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet" /> Cargas inteligentes a Flotilla (Strix)</h3>
      <p className="text-xs text-navy/40 mt-1">La IA separa cada PDF por unidad (pólizas, libre deuda, licencias…), completa vencimientos y los adjunta a la bitácora. Tras un par de confirmaciones sin cambios, se cargan solas.</p>
      {autos.length > 0 && (
        <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-800" data-testid="docshub-auto-banner">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold">Carga automática activada</span>: ya confirmaste {trust.min} cargas sin cambios, así que los próximos documentos de estos tipos se analizarán y cargarán solos a Flotilla, sin esperar tu confirmación.
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-2 ml-6">
            {autos.map((t) => (
              <span key={t.tipo} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-white/70 px-2.5 py-1">
                {TIPO_LABEL[t.tipo] || t.tipo}
                <button onClick={() => toggleTrust(t.tipo)} data-testid={`trust-pause-${t.tipo}`}
                  className="text-emerald-700/70 hover:text-emerald-900 underline decoration-dotted" title="Pausar: volver al modo con confirmación">pausar</button>
              </span>
            ))}
          </div>
        </div>
      )}
      {pausados.length > 0 && (
        <div className="mt-2 rounded-xl border border-navy/10 bg-navy/[0.03] px-3 py-2 text-[11px] text-navy/55 flex flex-wrap items-center gap-2" data-testid="docshub-paused-banner">
          <span className="font-medium">Carga automática en pausa</span> (se pide tu confirmación en cada carga):
          {pausados.map((t) => (
            <span key={t.tipo} className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 bg-white px-2.5 py-1">
              {TIPO_LABEL[t.tipo] || t.tipo}
              <button onClick={() => toggleTrust(t.tipo)} data-testid={`trust-resume-${t.tipo}`}
                className="text-violet hover:underline">reactivar</button>
            </span>
          ))}
        </div>
      )}
      {enProgreso.length > 0 && (
        <div className="mt-2 text-[11px] text-navy/45" data-testid="docshub-trust-progress">
          {enProgreso.map((t) => `${TIPO_LABEL[t.tipo] || t.tipo}: ${t.confirms} de ${trust.min} confirmaciones para activar la carga automática`).join(" · ")}
        </div>
      )}
      <div className="space-y-4 mt-3">
        {jobs.map((j) => {
          const [label, cls] = chip(j.status);
          return (
            <div key={j.id} className="border border-navy/10 rounded-xl p-3" data-testid={`job-${j.id}`}>
              <div className="flex items-center gap-2 flex-wrap text-sm">
                {j.status === "analizando" && <Loader2 className="w-3.5 h-3.5 animate-spin text-violet" />}
                <span className={`text-[11px] rounded-full px-2 py-0.5 font-medium ${cls}`} data-testid="job-status">{label}</span>
                <span className="text-navy/60">{(j.files || []).map((f) => f.name).join(" · ")}</span>
                <span className="text-[11px] text-navy/35 ml-auto">{String(j.created_at).slice(0, 16).replace("T", " ")}</span>
                <button onClick={async () => { try { await api.delete(`/docshub/jobs/${j.id}`); load(); } catch { toast.error("Error"); } }}
                  className="text-navy/25 hover:text-red-500" data-testid={`job-del-${j.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              {j.status === "error" && (
                <div className="text-xs text-red-600 mt-1 flex items-center gap-2">{j.error}
                  <button onClick={async () => { try { await api.post(`/docshub/jobs/${j.id}/reanalyze`); toast.success("Reintentando análisis…"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } }}
                    className="text-violet hover:underline" data-testid={`job-retry-${j.id}`}>reintentar</button>
                </div>
              )}
              {j.status === "preview" && (j.items || []).length > 0 && (j.items || []).every((it) => (it.detalle || "").startsWith("No se pudo interpretar")) ? (
                <div className="text-xs text-red-600 mt-2 flex items-center gap-3 flex-wrap" data-testid={`job-failed-${j.id}`}>
                  <span>La IA no pudo leer este documento (error temporal del servicio).</span>
                  <button onClick={async () => { try { await api.post(`/docshub/jobs/${j.id}/reanalyze`); toast.success("Reintentando análisis…"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } }}
                    className="text-violet hover:underline font-medium" data-testid={`job-reanalyze-${j.id}`}>Reanalizar</button>
                  <button onClick={async () => { try { await api.delete(`/docshub/jobs/${j.id}`); toast.success("Carga eliminada"); load(); } catch { toast.error("Error"); } }}
                    className="text-red-600 hover:underline font-medium" data-testid={`job-remove-${j.id}`}>Eliminar carga</button>
                </div>
              ) : j.status === "preview" && <JobItems job={j} onApplied={load} />}
              {String(j.status).startsWith("aplicado") && j.resumen && (
                <div className="text-xs text-navy/60 mt-2" data-testid="job-resumen">
                  {j.resumen.total} documento(s) cargado(s):
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(j.resumen.aplicados || []).map((a, i) => (
                      <span key={i} className={`rounded-full px-2 py-0.5 border text-[11px] ${a.deuda === "con_deuda" ? "border-red-300 bg-red-50 text-red-700" : "border-navy/10 bg-white/60"}`}>
                        {a.patente || a.titular} · {TIPO_LABEL[a.tipo] || a.tipo} · {a.vence}{a.unit_estado === "baja" ? " · (baja)" : ""}
                      </span>
                    ))}
                  </div>
                  {j.resumen.sin_unidad?.length > 0 && <div className="text-amber-600 mt-1">Sin unidad: {j.resumen.sin_unidad.join(", ")}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
